// src/components/payments/LicensePricing.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { paymentsService } from '../../services/paymentsService';
import { usePageTitle } from '../../hooks/usePageTitle';
import './LicensePricing.css';

const CSV_HEADERS = ['country_code', 'user_count', 'unit_price', 'total_price', 'setup_fee', 'currency', 'is_active'];

const emptyForm = {
    country_code: '',
    user_count: '',
    unit_price: '',
    total_price: '',
    setup_fee: '0',
    currency: 'USD',
    is_active: true
};

const LicensePricing = () => {
    usePageTitle('License Pricing');
    const [pricing, setPricing] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createForm, setCreateForm] = useState(emptyForm);

    const [uploading, setUploading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const fileInputRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        loadPricing();
    }, []);

    // Distinct countries actually present in the data, for the filter dropdown.
    const countryOptions = useMemo(() => {
        const codes = new Set();
        let hasGlobal = false;
        pricing.forEach((row) => {
            if (row.country_code) {
                codes.add(row.country_code);
            } else {
                hasGlobal = true;
            }
        });
        return {
            codes: Array.from(codes).sort(),
            hasGlobal
        };
    }, [pricing]);

    const filteredPricing = useMemo(() => {
        const needle = searchTerm.trim().toLowerCase();

        return pricing.filter((row) => {
            if (countryFilter === 'global' && row.country_code) return false;
            if (countryFilter !== 'all' && countryFilter !== 'global' && row.country_code !== countryFilter) return false;

            if (statusFilter === 'active' && !Number(row.is_active)) return false;
            if (statusFilter === 'inactive' && Number(row.is_active)) return false;

            if (!needle) return true;
            const haystack = [
                row.country_code || 'global',
                row.user_count,
                row.currency,
                row.unit_price,
                row.total_price,
                row.setup_fee
            ].join(' ').toLowerCase();
            return haystack.includes(needle);
        });
    }, [pricing, searchTerm, countryFilter, statusFilter]);

    const loadPricing = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await paymentsService.getLicensePricing();
            setPricing(data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const flashSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleCreate = async () => {
        setLoading(true);
        setError(null);
        try {
            await paymentsService.createLicensePricing({
                ...createForm,
                is_active: createForm.is_active ? 1 : 0
            });
            flashSuccess('Pricing tier created successfully!');
            setShowCreateForm(false);
            setCreateForm(emptyForm);
            await loadPricing();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row) => {
        setEditingId(row.id);
        setEditForm({ ...row, is_active: !!Number(row.is_active) });
        setError(null);
        setSuccess(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSave = async (id) => {
        setLoading(true);
        setError(null);
        try {
            await paymentsService.updateLicensePricing(id, {
                ...editForm,
                is_active: editForm.is_active ? 1 : 0
            });
            flashSuccess('Pricing tier updated successfully!');
            setEditingId(null);
            setEditForm({});
            await loadPricing();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (row) => {
        const label = `${row.country_code || 'Global'} / ${row.user_count} users`;
        if (!window.confirm(`Are you sure you want to delete the pricing tier for ${label}?`)) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await paymentsService.deleteLicensePricing(row.id);
            flashSuccess('Pricing tier deleted successfully!');
            await loadPricing();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (row) => {
        setLoading(true);
        setError(null);
        try {
            await paymentsService.updateLicensePricing(row.id, {
                is_active: Number(row.is_active) ? 0 : 1
            });
            flashSuccess(`Tier ${Number(row.is_active) ? 'deactivated' : 'activated'} successfully!`);
            await loadPricing();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError(null);
        setBulkResult(null);
        try {
            const result = await paymentsService.bulkUploadLicensePricing(file);
            setBulkResult(result);
            await loadPricing();
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const downloadTemplate = () => {
        const sampleRows = [
            ['', '5', '299.00', '1495.00', '750.00', 'USD', '1'],
            ['PK', '5', '299.62', '1499.00', '750.00', 'PKR', '1']
        ];
        const csv = [CSV_HEADERS, ...sampleRows].map((r) => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'license_pricing_template.csv';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    if (loading && !editingId && pricing.length === 0) {
        return (
            <div className="license-pricing">
                <div className="container">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading license pricing...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="license-pricing">
            <div className="container">
                <div className="page-header">
                    <div className="header-content">
                        <h1>License Pricing</h1>
                        <p>Manage per-country, per-staff-count subscription pricing tiers</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn btn-secondary" onClick={downloadTemplate}>
                            <i className="fas fa-download"></i>
                            CSV Template
                        </button>
                        <label className="btn btn-secondary upload-btn">
                            <i className="fas fa-file-csv"></i>
                            {uploading ? 'Uploading...' : 'Bulk Upload CSV'}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                disabled={uploading}
                                hidden
                            />
                        </label>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateForm(!showCreateForm)}
                        >
                            <i className="fas fa-plus"></i>
                            {showCreateForm ? 'Cancel' : 'Add Tier'}
                        </button>
                    </div>
                </div>

                {bulkResult && (
                    <div className="alert alert-success bulk-result">
                        <i className="fas fa-check-circle"></i>
                        <span>
                            Bulk upload complete: {bulkResult.created} created, {bulkResult.updated} updated, {bulkResult.skipped} skipped.
                            {bulkResult.errors?.length > 0 && (
                                <ul className="bulk-errors">
                                    {bulkResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            )}
                        </span>
                        <button onClick={() => setBulkResult(null)} className="alert-close">&times;</button>
                    </div>
                )}

                {showCreateForm && (
                    <div className="create-pricing-form">
                        <h3>Add New Pricing Tier</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Country Code (blank = global/USD default)</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    maxLength={2}
                                    value={createForm.country_code}
                                    onChange={(e) => setCreateForm({ ...createForm, country_code: e.target.value.toUpperCase() })}
                                    placeholder="e.g., PK"
                                />
                            </div>
                            <div className="form-group">
                                <label>Staff Count *</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="form-input"
                                    value={createForm.user_count}
                                    onChange={(e) => setCreateForm({ ...createForm, user_count: e.target.value })}
                                    placeholder="e.g., 5"
                                />
                            </div>
                            <div className="form-group">
                                <label>Currency *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={createForm.currency}
                                    onChange={(e) => setCreateForm({ ...createForm, currency: e.target.value.toUpperCase() })}
                                    placeholder="USD"
                                />
                            </div>
                            <div className="form-group">
                                <label>Unit Price (per staff/month) *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-input"
                                    value={createForm.unit_price}
                                    onChange={(e) => setCreateForm({ ...createForm, unit_price: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Total Price (monthly) *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-input"
                                    value={createForm.total_price}
                                    onChange={(e) => setCreateForm({ ...createForm, total_price: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Setup Fee</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-input"
                                    value={createForm.setup_fee}
                                    onChange={(e) => setCreateForm({ ...createForm, setup_fee: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_active}
                                        onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                                    />
                                    <span>Active</span>
                                </label>
                            </div>
                        </div>
                        <div className="form-actions">
                            <button className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreate}
                                disabled={loading || !createForm.user_count || !createForm.currency}
                            >
                                <i className="fas fa-plus"></i>
                                Create Tier
                            </button>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="alert-close">&times;</button>
                    </div>
                )}

                {success && (
                    <div className="alert alert-success">
                        <i className="fas fa-check-circle"></i>
                        <span>{success}</span>
                        <button onClick={() => setSuccess(null)} className="alert-close">&times;</button>
                    </div>
                )}

                <div className="pricing-filters">
                    <div className="search-box">
                        <i className="fas fa-search search-icon"></i>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Search by country, currency, staff count, price..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        className="form-input filter-select"
                        value={countryFilter}
                        onChange={(e) => setCountryFilter(e.target.value)}
                    >
                        <option value="all">All Countries</option>
                        {countryOptions.hasGlobal && <option value="global">Global</option>}
                        {countryOptions.codes.map((code) => (
                            <option key={code} value={code}>{code}</option>
                        ))}
                    </select>
                    <select
                        className="form-input filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    {(searchTerm || countryFilter !== 'all' || statusFilter !== 'all') && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setSearchTerm(''); setCountryFilter('all'); setStatusFilter('all'); }}
                        >
                            Clear Filters
                        </button>
                    )}
                    <span className="filter-count">
                        {filteredPricing.length} of {pricing.length} tiers
                    </span>
                </div>

                <div className="pricing-table-wrapper">
                    <table className="pricing-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Country</th>
                                <th>Staff Count</th>
                                <th>Currency</th>
                                <th>Unit Price</th>
                                <th>Total Price</th>
                                <th>Setup Fee</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPricing.map((row) => (
                                editingId === row.id ? (
                                    <tr key={row.id} className="editing-row">
                                        <td>
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={editForm.is_active}
                                                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                maxLength={2}
                                                value={editForm.country_code || ''}
                                                onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value.toUpperCase() })}
                                                placeholder="Global"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-input"
                                                value={editForm.user_count}
                                                onChange={(e) => setEditForm({ ...editForm, user_count: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editForm.currency}
                                                onChange={(e) => setEditForm({ ...editForm, currency: e.target.value.toUpperCase() })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-input"
                                                value={editForm.unit_price}
                                                onChange={(e) => setEditForm({ ...editForm, unit_price: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-input"
                                                value={editForm.total_price}
                                                onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-input"
                                                value={editForm.setup_fee}
                                                onChange={(e) => setEditForm({ ...editForm, setup_fee: e.target.value })}
                                            />
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn btn-success btn-sm" onClick={() => handleSave(row.id)} disabled={loading}>
                                                <i className="fas fa-check"></i>
                                            </button>
                                            <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit} disabled={loading}>
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={row.id}>
                                        <td>
                                            <button
                                                className={`status-badge ${Number(row.is_active) ? 'active' : 'inactive'}`}
                                                onClick={() => handleToggleActive(row)}
                                                title="Click to toggle status"
                                            >
                                                {Number(row.is_active) ? 'Active' : 'Inactive'}
                                            </button>
                                        </td>
                                        <td>
                                            {row.country_code ? (
                                                <code className="code-badge">{row.country_code}</code>
                                            ) : (
                                                <span className="text-muted">Global</span>
                                            )}
                                        </td>
                                        <td><strong>{row.user_count}</strong></td>
                                        <td>{row.currency}</td>
                                        <td>{Number(row.unit_price).toFixed(2)}</td>
                                        <td>{Number(row.total_price).toFixed(2)}</td>
                                        <td>{Number(row.setup_fee).toFixed(2)}</td>
                                        <td className="actions-cell">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleEdit(row)} disabled={loading}>
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(row)} disabled={loading}>
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>

                    {filteredPricing.length === 0 && pricing.length > 0 && (
                        <div className="empty-state">
                            <i className="fas fa-search"></i>
                            <h3>No Matching Tiers</h3>
                            <p>Try adjusting your search or filters</p>
                        </div>
                    )}

                    {pricing.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-tags"></i>
                            <h3>No Pricing Tiers</h3>
                            <p>Add a tier manually or bulk-upload a CSV to get started</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LicensePricing;
